import { useId } from "react";
import { assertNever } from "@ondo/shared";
import type { NotificationDelivery, NotificationType } from "@ondo/shared";

import { LoadingScreen } from "../lib/Loading.tsx";
import { useNotificationPreferencesController } from "./useNotificationPreferencesController.ts";
import {
  DELIVERY_OPTIONS,
  DIGEST_COPY,
  PREFERENCES_COPY,
} from "./notificationPreferences.constants.ts";
import type {
  DigestControl,
  PreferenceGroup,
  PreferenceRow,
} from "./notificationPreferences.model.ts";

import "./notificationPreferences.css";

/**
 * How each kind of notification reaches you: immediately, in the daily digest,
 * or not at all.
 *
 * THE SCREEN IS UNIFORM, and that is the ticket's answer rather than an
 * oversight: "every kind may be switched off… somebody may silence anything, a
 * remittance or a transfer of title included, and the consequence of having done
 * so is theirs." So there is no locked row, no minimum, and no kind rendered
 * differently from the rest. What the money and title rows get instead is a
 * SENTENCE when they are switched off, saying what the reader has stopped being
 * told — which is what makes placing the risk on them fair.
 *
 * It configures delivery and nothing else. Switching a kind off stops the
 * message and never the record: the settlement is still due and the title has
 * still moved, and both still appear on the screens that read those records.
 */
export function NotificationPreferences({
  principalOndoId = null,
  canWrite,
  isFirm = false,
}: {
  /** Whose preferences. Null is the reader's own — the inbox's parameter. */
  principalOndoId?: string | null;
  /** `atLeast(membership?.level, "admin")`. True on a personal workspace. */
  canWrite: boolean;
  isFirm?: boolean;
}) {
  const { view, choose, chooseHour } = useNotificationPreferencesController({
    principalOndoId,
    canWrite,
    isFirm,
  });

  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={PREFERENCES_COPY.loading} />;

    case "error":
      return (
        <p role="alert" className="prefs__error">
          {view.message}
        </p>
      );

    case "open":
      return (
        <div className="prefs">
          <p className="prefs__lead">{view.lead}</p>
          <p className="prefs__risk">{view.risk}</p>

          {view.readOnlyNote && <p className="prefs__note">{view.readOnlyNote}</p>}

          {view.error && (
            <p role="alert" className="prefs__error">
              {view.error}
            </p>
          )}
          {view.saved && (
            <p role="status" className="prefs__saved">
              {PREFERENCES_COPY.saved}
            </p>
          )}

          {view.digest && (
            <Digest
              digest={view.digest}
              canWrite={canWrite}
              isFirm={isFirm}
              onChoose={chooseHour}
            />
          )}

          {view.groups.map((group) => (
            <Group key={group.id} group={group} canWrite={canWrite} onChoose={choose} />
          ))}
        </div>
      );

    default:
      return assertNever(view);
  }
}

function Group({
  group,
  canWrite,
  onChoose,
}: {
  group: PreferenceGroup;
  canWrite: boolean;
  onChoose: (type: NotificationType, delivery: NotificationDelivery) => void;
}) {
  const titleId = useId();

  return (
    // Named by its own heading, which is what makes it a landmark rather than an
    // anonymous section — `ProfilePanels`' rule, and it costs one `useId`.
    <section className="prefs__panel" aria-labelledby={titleId}>
      <h2 className="prefs__panel-title" id={titleId}>
        {group.title}
      </h2>
      <p className="prefs__panel-note">{group.blurb}</p>

      <ul className="prefs__rows">
        {group.rows.map((row) => (
          <Row key={row.type} row={row} canWrite={canWrite} onChoose={onChoose} />
        ))}
      </ul>
    </section>
  );
}

function Row({
  row,
  canWrite,
  onChoose,
}: {
  row: PreferenceRow;
  canWrite: boolean;
  onChoose: (type: NotificationType, delivery: NotificationDelivery) => void;
}) {
  const id = useId();

  return (
    <li className="prefs__row">
      {/*
        A real `<fieldset>` of real radios, not a row of buttons with
        `role="radio"`. `DetailsForm` made the same choice and said why: the
        buttons "look identical but announce as eight unrelated controls with no
        grouping and no checked state" — and native radios bring arrow-key
        movement with them, which a hand-rolled radiogroup owes and this
        codebase's one example does not pay.
      */}
      <fieldset className="prefs__choice-set" disabled={!canWrite || row.saving}>
        <legend className="prefs__row-label">{row.label}</legend>

        <div className="prefs__choices">
          {DELIVERY_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`prefs__choice${
                row.delivery === option.value ? " prefs__choice--on" : ""
              }`}
              title={option.hint}
            >
              <input
                className="prefs__choice-input"
                type="radio"
                name={`${id}-${row.type}`}
                value={option.value}
                checked={row.delivery === option.value}
                onChange={() => onChoose(row.type, option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {/*
        The consequence, when there is one. `role="status"` because it appears in
        response to the reader's own act and should be announced when it does —
        it is the whole reason the risk is fair to place on them, so arriving
        silently for a screen-reader user would defeat it.
      */}
      {row.silenced && (
        <p className="prefs__silenced" role="status">
          {row.silenced}
        </p>
      )}
    </li>
  );
}

/**
 * WHEN the daily digest arrives — OND-96.
 *
 * A `<select>` AND NOT TWENTY-FOUR RADIOS, which is the one place this screen
 * departs from the delivery rows above. Those are three mutually exclusive
 * choices a reader compares side by side, and native radios are what announce
 * them as one question; twenty-four of anything is a list to pick from, and a
 * select is the control a browser already gives arrow keys, type-ahead and a
 * closed resting state to.
 *
 * EVERY LABEL SAYS UTC, which is the owner's instruction rather than this file's
 * taste: "label it clearly. This is one global clock, not 09:00 in each reader's
 * local timezone."
 */
function Digest({
  digest,
  canWrite,
  isFirm,
  onChoose,
}: {
  digest: DigestControl;
  canWrite: boolean;
  isFirm: boolean;
  onChoose: (hourUtc: number) => void;
}) {
  const titleId = useId();
  const selectId = useId();

  return (
    // Named by its own heading, which is what makes it a landmark rather than an
    // anonymous section — `Group` below makes the same choice for the same cost.
    <section className="prefs__panel" aria-labelledby={titleId}>
      <h2 className="prefs__panel-title" id={titleId}>
        {DIGEST_COPY.title}
      </h2>
      <p className="prefs__panel-note">
        {isFirm ? DIGEST_COPY.firmBlurb : DIGEST_COPY.blurb}
      </p>

      <div className="prefs__digest">
        <label className="prefs__digest-label" htmlFor={selectId}>
          {DIGEST_COPY.label}
        </label>
        <select
          id={selectId}
          className="prefs__digest-select"
          value={digest.hourUtc}
          disabled={!canWrite || digest.saving}
          onChange={(event) => onChoose(Number(event.target.value))}
        >
          {digest.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/*
        `role="status"` for the reason the silenced line below has it: this
        sentence is the difference between a time somebody chose and the default
        they are on, and it changes in response to their own act.
      */}
      <p className="prefs__digest-note" role="status">
        {digest.note}
      </p>
      <p className="prefs__panel-note">{DIGEST_COPY.utcNote}</p>
      <p className="prefs__panel-note">{DIGEST_COPY.noOffNote}</p>
    </section>
  );
}
